import { useEffect, useRef } from 'react'
import { useGetList } from 'react-admin'
import { GemeenteResponse } from '../types/apiRequests'

const STORAGE_KEY = 'prognoses.last_gemeente_sid'

/**
 * Restore the last-selected gemeente from ``localStorage`` so the filter
 * survives reloads and navigation between Stad / Gebied / Overige Data.
 *
 * Earlier versions required the caller to pass the full gemeenten list; that
 * coupled the hook to a ``useGetList(perPage: 99)`` which silently capped at
 * the first 99 alphabetically (everything past "G" was unreachable). This
 * version fetches the single stored sid via the data provider so it works
 * for any gemeente regardless of how the picker loads its options.
 *
 * Call `rememberGemeente(gem)` whenever the active gemeente changes so the
 * choice is persisted for the next visit.
 */
export const useLastGemeente = (
  selectedGemeente: GemeenteResponse | null,
  onSelect: (gem: GemeenteResponse) => void,
): void => {
  const appliedRef = useRef(false)
  const storedSid = readStoredSid()

  // Only fire the query when there's something to look up; the dataprovider
  // would otherwise return an empty list for ``sid: -1``.
  const shouldFetch = storedSid !== null && !selectedGemeente && !appliedRef.current
  const { data: matches } = useGetList<GemeenteResponse>(
    'gemeenten',
    {
      // Explicit sort is required — react-admin defaults to ``id DESC`` but
      // ``id`` is not in ``GemeenteSortFields`` so the API would 400.
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'sid', order: 'ASC' },
      filter: { sid: storedSid ?? 0 },
    },
    { enabled: shouldFetch },
  )

  useEffect(() => {
    if (appliedRef.current || selectedGemeente) return
    if (matches && matches.length === 1) {
      appliedRef.current = true
      onSelect(matches[0])
    }
  }, [matches, selectedGemeente, onSelect])
}

export const rememberGemeente = (gem: GemeenteResponse | null): void => {
  try {
    if (gem) {
      window.localStorage.setItem(STORAGE_KEY, String(gem.sid))
    }
  } catch {
    // localStorage can throw in private-browsing / quota-exceeded scenarios.
    // Silently ignore — losing the preference is not worth surfacing as an error.
  }
}

const readStoredSid = (): number | null => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  } catch {
    return null
  }
}
